/**
 * FSD Part 6 — CRM (docs §19161). C17: one section per FSD feature
 * specification — 6.2.1 (CRM-F01) through 6.2.5 (CRM-F05), section titles
 * from `nav.fsd.*`.
 *
 * C17 gave six existing CRM screens their first door: lead scoring models
 * and routing rules (FSD text lines 7199, 7250), the forecast board and
 * win/loss analysis (FSD text lines 7392, 7519), contacts, and marketing
 * segments. Each uses its route's own title key and the exact permission its
 * route declares (`modules/crm/routes.ts`). Party consent and suppressions
 * moved here from Platform: they are the contact-consent half of CRM-F04.
 *
 * Cases, the knowledge base, SLA policies and the service report are NOT
 * here: they are Service Management (Part 13), in `p13-service.ts`.
 *
 * Item ids are stable and must stay so: favourites and recents in
 * localStorage key off them. Sections moved; items did not change.
 */
import type { NavGroup } from '../types'
import { CRM_PERMISSIONS } from '@/modules/crm/permissions'
import { PARTY_PERMISSIONS } from '@/modules/party/permissions'

export const P06_CRM_GROUPS: NavGroup[] = [
  {
    id: 's-6.2.1',
    kind: 'feature',
    fsd: '6.2.1',
    code: 'CRM-F01',
    labelKey: 'nav.fsd.s6_2_1.short',
    fullTitleKey: 'nav.fsd.s6_2_1.full',
    items: [
      {
        id: 'crm-leads',
        labelKey: 'crm.nav.leads',
        to: '/crm/leads',
        icon: 'users',
        permission: CRM_PERMISSIONS.leadRead,
        descriptionKey: 'nav.items.crmLeads.description',
      },
      {
        id: 'crm-score-models',
        labelKey: 'crm.c5.routeTitles.scoring',
        to: '/crm/score-models',
        icon: 'star',
        permission: CRM_PERMISSIONS.leadRead,
      },
      {
        id: 'crm-routing-rules',
        labelKey: 'crm.c5.routeTitles.routing',
        to: '/crm/routing-rules',
        icon: 'link',
        permission: CRM_PERMISSIONS.leadRead,
      },
      {
        id: 'crm-conversion-report',
        labelKey: 'crm.routeTitles.conversionReport',
        to: '/crm/reports/conversion',
        icon: 'history',
        permission: CRM_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-6.2.2',
    kind: 'feature',
    fsd: '6.2.2',
    code: 'CRM-F02',
    labelKey: 'nav.fsd.s6_2_2.short',
    fullTitleKey: 'nav.fsd.s6_2_2.full',
    items: [
      {
        id: 'crm-opportunities',
        labelKey: 'crm.nav.opportunities',
        to: '/crm/opportunities',
        icon: 'star',
        permission: CRM_PERMISSIONS.opportunityRead,
        descriptionKey: 'nav.items.crmOpportunities.description',
      },
      {
        id: 'crm-forecast',
        labelKey: 'crm.c5.routeTitles.forecast',
        to: '/crm/forecast',
        icon: 'calendar',
        permission: CRM_PERMISSIONS.reportRead,
      },
      {
        id: 'crm-win-loss',
        labelKey: 'crm.c5.routeTitles.winLoss',
        to: '/crm/win-loss',
        icon: 'check',
        permission: CRM_PERMISSIONS.reportRead,
      },
      {
        id: 'crm-pipeline-report',
        labelKey: 'crm.routeTitles.pipelineReport',
        to: '/crm/reports/pipeline',
        icon: 'scale',
        permission: CRM_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-6.2.3',
    kind: 'feature',
    fsd: '6.2.3',
    code: 'CRM-F03',
    labelKey: 'nav.fsd.s6_2_3.short',
    fullTitleKey: 'nav.fsd.s6_2_3.full',
    items: [
      {
        id: 'crm-accounts',
        labelKey: 'crm.c5.nav.accounts',
        to: '/crm/accounts',
        icon: 'building',
        permission: CRM_PERMISSIONS.accountRead,
        descriptionKey: 'nav.items.crmAccounts.description',
      },
    ],
  },
  {
    id: 's-6.2.4',
    kind: 'feature',
    fsd: '6.2.4',
    code: 'CRM-F04',
    labelKey: 'nav.fsd.s6_2_4.short',
    fullTitleKey: 'nav.fsd.s6_2_4.full',
    items: [
      {
        id: 'crm-contacts',
        labelKey: 'crm.c5.routeTitles.contacts',
        to: '/crm/contacts',
        icon: 'user',
        permission: CRM_PERMISSIONS.accountRead,
      },
      {
        id: 'party-consent',
        labelKey: 'party.nav.consent',
        to: '/party/consent/records',
        icon: 'check',
        permission: PARTY_PERMISSIONS.consentRead,
        descriptionKey: 'nav.items.partyConsent.description',
      },
      {
        id: 'party-suppressions',
        labelKey: 'party.nav.suppressions',
        to: '/party/suppressions',
        icon: 'help',
        permission: PARTY_PERMISSIONS.suppressionRead,
        descriptionKey: 'nav.items.partySuppressions.description',
      },
    ],
  },
  {
    id: 's-6.2.5',
    kind: 'feature',
    fsd: '6.2.5',
    code: 'CRM-F05',
    labelKey: 'nav.fsd.s6_2_5.short',
    fullTitleKey: 'nav.fsd.s6_2_5.full',
    items: [
      {
        id: 'crm-campaigns',
        labelKey: 'crm.c5.nav.campaigns',
        to: '/crm/campaigns',
        icon: 'refresh',
        permission: CRM_PERMISSIONS.campaignRead,
        descriptionKey: 'nav.items.crmCampaigns.description',
      },
      {
        id: 'crm-segments',
        labelKey: 'crm.c5.routeTitles.segments',
        to: '/crm/segments',
        icon: 'sitemap',
        permission: CRM_PERMISSIONS.campaignRead,
      },
    ],
  },
]
